// Client-side JSON-Schema validation of alert decision trees.
//
// This is Layer B of the two-layer save flow (see the validation design spec):
// after the friendly completeness checks pass and `toSchemaTree` runs, we
// validate the serialized tree against the *same* YAML schema the Java gateway
// and the PL/pgSQL compiler use. It should effectively never fire — if it does,
// the serializer or the strict types have a bug, and we block the save and log
// loudly rather than ship invalid JSON.
//
// AJV setup mirrors `Alerts/ts-compiler/compiler.ts:44-66`, with one difference:
// `allErrors: true` so a Layer-B failure logs every problem, not just the first.

import Ajv2020, { type ErrorObject, type ValidateFunction } from 'ajv/dist/2020'
import addFormats from 'ajv-formats'
import yaml from 'js-yaml'

// Bun (ts-compiler) imports .yaml natively; Vite does not. Load the raw text
// via Vite's `?raw` import and parse with js-yaml. The copies keep their $ids,
// so cross-file $refs resolve exactly as in compiler.ts.
import rootYaml from './schema/alert-decision-tree.yaml?raw'
import v1TreeYaml from './schema/v1/alert-decision-tree.yaml?raw'
import commonYaml from './schema/v1/common.yaml?raw'
import nodesYaml from './schema/v1/nodes.yaml?raw'
import conditionYaml from './schema/v1/condition-parameters.yaml?raw'
import triggerYaml from './schema/v1/trigger-parameters.yaml?raw'

const ALERT_TREE_SCHEMA_ID =
  'https://sn.rimonim.tech/schemas/events/alert-decision-tree.yaml'

const SCHEMAS = [
  rootYaml,
  v1TreeYaml,
  commonYaml,
  nodesYaml,
  conditionYaml,
  triggerYaml,
].map(text => yaml.load(text) as Record<string, unknown>)

function createValidator(): ValidateFunction {
  const ajv = new Ajv2020({ allErrors: true, strict: false })
  addFormats(ajv) // needed for format: time / duration
  for (const schema of SCHEMAS) ajv.addSchema(schema)
  const validate = ajv.getSchema(ALERT_TREE_SCHEMA_ID)
  if (!validate) {
    throw new Error(`Schema ${ALERT_TREE_SCHEMA_ID} not registered with Ajv`)
  }
  return validate
}

// Compiled once at module load; reused for every validateTree call.
const validateAlertTreeSchema = createValidator()

export type TreeValidationResult =
  | { valid: true }
  | { valid: false; errors: ErrorObject[] }

export function validateTree(tree: unknown): TreeValidationResult {
  const valid = validateAlertTreeSchema(tree)
  return valid
    ? { valid: true }
    : { valid: false, errors: validateAlertTreeSchema.errors ?? [] }
}

/**
 * Decode an AJV `instancePath` back to the id of the nearest enclosing tree
 * node, so a Layer-B failure can be logged against a specific React-Flow node.
 * Primarily a developer aid — users should never reach a Layer-B failure.
 *
 * Example: instancePath `/root/children/0/condition/operator` → the id of the
 * condition node at `root.children[0]`.
 */
export function nodeIdAtInstancePath(
  tree: unknown,
  instancePath: string
): string | null {
  const rootId =
    isObject(tree) && isObject(tree.root) && typeof tree.root.id === 'string'
      ? tree.root.id
      : null

  if (!instancePath) return rootId

  let current: unknown = tree
  let lastId = rootId

  for (const rawSegment of instancePath.split('/').filter(Boolean)) {
    // JSON Pointer escaping: ~1 → /, ~0 → ~
    const key = rawSegment.replace(/~1/g, '/').replace(/~0/g, '~')
    if (Array.isArray(current)) {
      current = current[Number(key)]
    } else if (isObject(current)) {
      current = current[key]
    } else {
      break
    }
    if (isObject(current) && typeof current.id === 'string') {
      lastId = current.id
    }
  }

  return lastId
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}
